/**
 * Auth state management — Sprint 8.0.
 *
 * Manages OAuth 2.1 PKCE flow, token storage, auto-refresh, and logout.
 * Access tokens are kept in-memory only. The refresh token is persisted
 * in localStorage so sessions survive hard refreshes.
 */

const API_BASE = import.meta.env.VITE_MCP_ENDPOINT
  ? new URL(import.meta.env.VITE_MCP_ENDPOINT).origin
  : "https://dev.kuvio.ai";

export interface AuthUser {
  id: string;
  email: string;
  name: string;
  avatar_url: string | null;
  workspaces: Array<{
    id: string;
    slug: string;
    name: string;
    plan: string;
    workspace_type: string;
    role: "owner" | "member";
  }>;
}

interface TokenSet {
  accessToken: string;
  refreshToken: string;
  expiresAt: number;
}

let tokens: TokenSet | null = null;
let currentUser: AuthUser | null = null;
let refreshTimer: ReturnType<typeof setTimeout> | null = null;
const listeners = new Set<() => void>();

function notify(): void {
  listeners.forEach((fn) => fn());
}

export function subscribe(fn: () => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

export function getAccessToken(): string | null {
  return tokens?.accessToken ?? null;
}

export function getUser(): AuthUser | null {
  return currentUser;
}

export function isAuthenticated(): boolean {
  return tokens !== null && Date.now() < tokens.expiresAt;
}

export function patchWorkspaceName(workspaceId: string, name: string): void {
  if (!currentUser) return;
  const ws = currentUser.workspaces.find((w) => w.id === workspaceId);
  if (ws) {
    ws.name = name;
    currentUser = { ...currentUser, workspaces: [...currentUser.workspaces] };
    notify();
  }
}

// ---------------------------------------------------------------------------
// OAuth 2.1 PKCE flow
// ---------------------------------------------------------------------------

function generateRandomString(length: number): string {
  const bytes = crypto.getRandomValues(new Uint8Array(length));
  return Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("").slice(0, length);
}

async function sha256(plain: string): Promise<ArrayBuffer> {
  const encoder = new TextEncoder();
  return crypto.subtle.digest("SHA-256", encoder.encode(plain));
}

function base64UrlEncode(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let str = "";
  for (const b of bytes) str += String.fromCharCode(b);
  return btoa(str).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

export async function startLogin(provider?: "google"): Promise<void> {
  const state = generateRandomString(32);
  const codeVerifier = generateRandomString(64);
  const codeChallenge = base64UrlEncode(await sha256(codeVerifier));

  localStorage.setItem("kuvio_pkce_state", state);
  localStorage.setItem("kuvio_pkce_verifier", codeVerifier);

  const clientId = "kuvio-console";
  const redirectUri = `${window.location.origin}/auth/callback`;

  if (provider === "google") {
    const params = new URLSearchParams({
      state,
      client_id: clientId,
      redirect_uri: redirectUri,
      code_challenge: codeChallenge,
      code_challenge_method: "S256",
    });
    window.location.href = `${API_BASE}/auth/google?${params}`;
    return;
  }

  const params = new URLSearchParams({
    response_type: "code",
    client_id: clientId,
    redirect_uri: redirectUri,
    state,
    code_challenge: codeChallenge,
    code_challenge_method: "S256",
  });
  window.location.href = `${API_BASE}/auth/authorize?${params}`;
}

/**
 * Email OTP — step 1: request a 6-digit code.
 *
 * Sets up the PKCE/state that the later token exchange (handleCallback) needs,
 * exactly like the Google flow, then asks the server to email a code. Returns
 * a challenge_id used by verifyOtp().
 */
export async function startOtp(
  email: string,
): Promise<{ ok: boolean; challengeId?: string; error?: string }> {
  const state = generateRandomString(32);
  const codeVerifier = generateRandomString(64);
  const codeChallenge = base64UrlEncode(await sha256(codeVerifier));

  localStorage.setItem("kuvio_pkce_state", state);
  localStorage.setItem("kuvio_pkce_verifier", codeVerifier);

  const clientId = "kuvio-console";
  const redirectUri = `${window.location.origin}/auth/callback`;

  try {
    const resp = await fetch(`${API_BASE}/auth/request-otp`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({
        email,
        state,
        client_id: clientId,
        redirect_uri: redirectUri,
        code_challenge: codeChallenge,
        code_challenge_method: "S256",
      }),
    });
    const data = (await resp.json()) as { ok: boolean; challenge_id?: string; error?: string };
    return { ok: data.ok, challengeId: data.challenge_id, error: data.error };
  } catch {
    return { ok: false, error: "network_error" };
  }
}

/**
 * Email OTP — step 2: verify the code and complete sign-in.
 *
 * On success the server returns a callback URL containing the authorization
 * code; we extract it and run the standard PKCE token exchange (handleCallback)
 * to obtain access + refresh tokens.
 */
export async function verifyOtp(
  challengeId: string,
  code: string,
): Promise<{ ok: boolean; error?: string; attemptsRemaining?: number }> {
  let data: { ok: boolean; redirect_uri?: string; error?: string; attempts_remaining?: number };
  try {
    const resp = await fetch(`${API_BASE}/auth/verify-otp`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ challenge_id: challengeId, code }),
    });
    data = (await resp.json()) as typeof data;
  } catch {
    return { ok: false, error: "network_error" };
  }

  if (!data.ok || !data.redirect_uri) {
    return { ok: false, error: data.error ?? "verify_failed", attemptsRemaining: data.attempts_remaining };
  }

  const url = new URL(data.redirect_uri);
  const authCode = url.searchParams.get("code");
  const state = url.searchParams.get("state") ?? "";
  if (!authCode) return { ok: false, error: "no_code" };

  const success = await handleCallback(authCode, state);
  return success ? { ok: true } : { ok: false, error: "exchange_failed" };
}

// ---------------------------------------------------------------------------
// Token exchange & refresh
// ---------------------------------------------------------------------------

export async function handleCallback(code: string, state: string): Promise<boolean> {
  const savedState = localStorage.getItem("kuvio_pkce_state");
  const codeVerifier = localStorage.getItem("kuvio_pkce_verifier");

  if (!savedState || savedState !== state) {
    console.error("[auth] State mismatch");
    return false;
  }

  localStorage.removeItem("kuvio_pkce_state");
  localStorage.removeItem("kuvio_pkce_verifier");

  const resp = await fetch(`${API_BASE}/auth/token`, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({
      grant_type: "authorization_code",
      code,
      code_verifier: codeVerifier,
      client_id: "kuvio-console",
      redirect_uri: `${window.location.origin}/auth/callback`,
    }),
  });

  if (!resp.ok) {
    console.error("[auth] Token exchange failed:", resp.status);
    return false;
  }

  const data = await resp.json() as {
    access_token: string;
    refresh_token: string;
    expires_in: number;
  };

  setTokens(data.access_token, data.refresh_token, data.expires_in);
  await fetchUserInfo();
  return true;
}

/**
 * Spec 10.2.1.2 — exchange a server-issued (challenge-less) approval-link code.
 * Unlike handleCallback there is no client-initiated PKCE flow on this device
 * (the user arrived via a magic link), so there is no saved state/verifier to
 * check — the code carries no code_challenge, so the token endpoint skips PKCE.
 */
export async function handleApprovalCallback(code: string): Promise<boolean> {
  const resp = await fetch(`${API_BASE}/auth/token`, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ grant_type: "authorization_code", code, client_id: "kuvio-console" }),
  });
  if (!resp.ok) {
    console.error("[auth] approval token exchange failed:", resp.status);
    return false;
  }
  const data = (await resp.json()) as { access_token: string; refresh_token: string; expires_in: number };
  setTokens(data.access_token, data.refresh_token, data.expires_in);
  await fetchUserInfo();
  return true;
}

const REFRESH_TOKEN_KEY = "kuvio_refresh_token";

function setTokens(accessToken: string, refreshToken: string, expiresIn: number): void {
  tokens = {
    accessToken,
    refreshToken,
    expiresAt: Date.now() + expiresIn * 1000,
  };

  try { localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken); } catch { /* private browsing */ }

  if (refreshTimer) clearTimeout(refreshTimer);
  const refreshIn = Math.max((expiresIn - 60) * 1000, 10_000);
  refreshTimer = setTimeout(() => void refreshAccessToken(), refreshIn);

  notify();
}

async function refreshAccessToken(): Promise<boolean> {
  if (!tokens?.refreshToken) return false;

  try {
    const resp = await fetch(`${API_BASE}/auth/token`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({
        grant_type: "refresh_token",
        refresh_token: tokens.refreshToken,
        client_id: "kuvio-console",
      }),
    });

    if (!resp.ok) {
      logout();
      return false;
    }

    const data = await resp.json() as {
      access_token: string;
      refresh_token: string;
      expires_in: number;
    };

    setTokens(data.access_token, data.refresh_token, data.expires_in);
    return true;
  } catch {
    logout();
    return false;
  }
}

async function fetchUserInfo(): Promise<void> {
  if (!tokens) return;

  try {
    const resp = await fetch(`${API_BASE}/auth/userinfo`, {
      headers: { Authorization: `Bearer ${tokens.accessToken}` },
    });

    if (resp.ok) {
      currentUser = await resp.json() as AuthUser;
      notify();
    }
  } catch {
    // best-effort
  }
}

// ---------------------------------------------------------------------------
// Logout
// ---------------------------------------------------------------------------

/**
 * Fetch wrapper that injects the current Bearer token. Use for all
 * authenticated API calls outside the MCP client (AI proxy, meetings, etc.).
 */
export function authFetch(input: RequestInfo | URL, init?: RequestInit): Promise<Response> {
  const token = getAccessToken();
  if (!token) return fetch(input, init);
  const headers = new Headers(init?.headers);
  if (!headers.has("authorization")) {
    headers.set("authorization", `Bearer ${token}`);
  }
  return fetch(input, { ...init, headers });
}

export async function restoreSession(): Promise<boolean> {
  const stored = localStorage.getItem(REFRESH_TOKEN_KEY);
  if (!stored) return false;

  tokens = { accessToken: "", refreshToken: stored, expiresAt: 0 };
  const ok = await refreshAccessToken();
  if (ok) {
    await fetchUserInfo();
  }
  return ok;
}

// ---------------------------------------------------------------------------
// Workspace management
// ---------------------------------------------------------------------------

export async function switchWorkspace(workspaceId: string): Promise<boolean> {
  if (!tokens) return false;

  try {
    const resp = await fetch(`${API_BASE}/auth/workspaces/switch`, {
      method: "POST",
      headers: {
        "content-type": "application/json",
        Authorization: `Bearer ${tokens.accessToken}`,
      },
      body: JSON.stringify({ workspace_id: workspaceId }),
    });

    if (!resp.ok) return false;

    const data = await resp.json() as {
      access_token: string;
      refresh_token: string;
      expires_in: number;
    };

    setTokens(data.access_token, data.refresh_token, data.expires_in);
    await fetchUserInfo();
    return true;
  } catch {
    return false;
  }
}

export async function createNewWorkspace(name: string): Promise<{ id: string; slug: string; name: string } | null> {
  if (!tokens) return null;

  try {
    const resp = await fetch(`${API_BASE}/auth/workspaces/create`, {
      method: "POST",
      headers: {
        "content-type": "application/json",
        Authorization: `Bearer ${tokens.accessToken}`,
      },
      body: JSON.stringify({ name }),
    });

    if (!resp.ok) return null;
    const workspace = await resp.json() as { id: string; slug: string; name: string };

    // Switch to the new workspace
    await switchWorkspace(workspace.id);
    return workspace;
  } catch {
    return null;
  }
}

export function getCurrentWorkspaceId(): string | null {
  if (!tokens?.accessToken) return null;
  try {
    const payload = JSON.parse(atob(tokens.accessToken.split(".")[1]!));
    return payload.wid ?? null;
  } catch {
    return null;
  }
}

export async function logout(): Promise<void> {
  if (tokens?.refreshToken) {
    try {
      await fetch(`${API_BASE}/auth/logout`, {
        method: "POST",
        headers: {
          "content-type": "application/json",
          Authorization: `Bearer ${tokens.accessToken}`,
        },
        body: JSON.stringify({ refresh_token: tokens.refreshToken }),
      });
    } catch {
      // best-effort revocation
    }
  }

  tokens = null;
  currentUser = null;
  if (refreshTimer) clearTimeout(refreshTimer);
  refreshTimer = null;
  try { localStorage.removeItem(REFRESH_TOKEN_KEY); } catch { /* private browsing */ }
  notify();
}

